import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AIOverviewCard from 'src/App/Dashboards/Market/Components/Cards/AIOverviewCard';
import Body from 'src/App/Dashboards/Market/Components/Text/Body';
import { VERIFIED_MARKETS_MESSAGE } from './HotelSupplyAndAvailabilityCard';
import { COLOR_TEXT_2, COLOR_PRIMARY_2 } from 'src/utils/colors';
import { COLOR_VERIFIED_TICK } from 'src/utils/const';
import {
  MARKET_WIDE_BADGE_TOOLTIP,
  VERIFIED_LOCK_BADGE_TOOLTIP,
  BADGE_PILL_SX,
} from './overviewSectionBadgeTooltips';

export interface VerifiedMarketSectionProps {
  title: string;
  /** Optional node rendered next to the title (e.g. Booking.com icon). */
  titleAdornment?: React.ReactNode;
  summary?: string | string[];
  isVerifiedMarket: boolean;
  isMobileView?: boolean;
  handleOnSampleClick?: (plan: string) => void;
  children?: React.ReactNode;
  showMarketWideBadge?: boolean;
  filterApplied?: boolean;
}

/**
 * Section wrapper for Market Intelligence content that is only available in verified markets.
 * When the market is not verified, shows the standard verified-markets-only message instead of children.
 */
const VerifiedMarketSection: React.FC<VerifiedMarketSectionProps> = ({
  title,
  titleAdornment,
  summary,
  isVerifiedMarket,
  isMobileView = false,
  handleOnSampleClick = () => {},
  children,
  showMarketWideBadge = false,
  filterApplied = false,
}) => {
  const summaryParagraphs = Array.isArray(summary) ? summary : summary ? [summary] : [];
  const showMarketWide = showMarketWideBadge && filterApplied;
  const showLock = !isVerifiedMarket;

  return (
    <Box className="section--spacing" sx={{ width: '100%' }}>
      <Box
        className="subsection--spacing"
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h5" className="marketTitle" sx={{ display: 'block' }}>
          {title}
        </Typography>
        {titleAdornment}
        {showMarketWide && (
          <Tooltip title={MARKET_WIDE_BADGE_TOOLTIP} arrow placement="top">
            <Box component="span" sx={{ ...BADGE_PILL_SX, backgroundColor: COLOR_PRIMARY_2, color: '#fff' }}>
              <InfoOutlinedIcon sx={{ fontSize: '0.875rem' }} />
              Filter not applied
            </Box>
          </Tooltip>
        )}
        {showLock && (
          <Tooltip title={VERIFIED_LOCK_BADGE_TOOLTIP} arrow placement="top">
            <Box component="span" sx={{ ...BADGE_PILL_SX, backgroundColor: COLOR_VERIFIED_TICK, color: '#fff' }}>
              <LockIcon sx={{ fontSize: '0.875rem' }} />
              Verified Markets Only
            </Box>
          </Tooltip>
        )}
      </Box>
      {summaryParagraphs.length > 0 && (
        <Box className="subsection--spacing">
          <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
            {summaryParagraphs.map((paragraph, index) => (
              <Body
                key={index}
                handleOnSampleClick={handleOnSampleClick}
                sx={index > 0 ? { mt: 1, mb: -1 } : undefined}
              >
                {paragraph}
              </Body>
            ))}
          </AIOverviewCard>
        </Box>
      )}
      <Box className="subsection--spacing" sx={{ width: '100%' }}>
        {isVerifiedMarket ? (
          children
        ) : (
          <AIOverviewCard isLoading={false} isMobileView={isMobileView}>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_2 }}>
              {VERIFIED_MARKETS_MESSAGE}
            </Typography>
          </AIOverviewCard>
        )}
      </Box>
    </Box>
  );
};

export default VerifiedMarketSection;
